/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/useQaSelection.ts
 * FILE_ID: JOBHUB-QA-SELECTION-SCOPE-001
 * PURPOSE: Accumulating selection across areas, checklists AND checks.
 * WHY: The mockup "Select" rules: picking two checks then an area must ADD,
 *      never reset. The old single-scope model dropped the previous set on
 *      every scope switch (operator regression report, 2026-09-16).
 * LANDMINE: A stale id from another inspection must never be posted — prune
 *      against the visible list on every read (visibleOf), same as
 *      useQaBulkSelection.
 * LANDMINE: A ticked checklist or area includes every check underneath.
 *      Unticking one check records an exclusion. It does not clear the header.
 *      Unticking the header clears those children. Select all or Clear drops
 *      every exclusion.
 */
import { useCallback, useMemo, useRef, useState } from 'react'

export type QaSelectionScope = 'areas' | 'checklists' | 'checks'

type QaSelectionSets = Readonly<Record<QaSelectionScope, ReadonlySet<string>>>

const EMPTY: QaSelectionSets = {
  areas: new Set<string>(),
  checklists: new Set<string>(),
  checks: new Set<string>(),
}

export function useQaSelection() {
  const [sets, setSets] = useState<QaSelectionSets>(EMPTY)
  const [excluded, setExcluded] = useState<ReadonlySet<string>>(new Set())
  const [touched, setTouched] = useState(false)
  const setsRef = useRef(sets)
  setsRef.current = sets

  const toggle = useCallback((scope: QaSelectionScope, id: string) => {
    setTouched(true)
    const removing = setsRef.current[scope].has(id)
    if (removing && (scope === 'checklists' || scope === 'areas')) setExcluded(new Set())
    setSets((current) => {
      const copy = new Set(current[scope])
      if (copy.has(id)) copy.delete(id)
      else copy.add(id)
      return { ...current, [scope]: copy }
    })
  }, [])

  const setHeader = useCallback((
    scope: 'areas' | 'checklists',
    id: string,
    on: boolean,
    children: {
      itemIds: readonly string[]
      checklistIds?: readonly string[]
      coveredByArea?: boolean
    },
  ) => {
    setTouched(true)
    const itemIds = children.itemIds
    const checklistIds = children.checklistIds ?? []
    const coveredByArea = Boolean(children.coveredByArea)
    setSets((current) => {
      const areas = new Set(current.areas)
      const checklists = new Set(current.checklists)
      const checks = new Set(current.checks)
      const header = scope === 'areas' ? areas : checklists
      if (on) header.add(id)
      else header.delete(id)
      if (scope === 'areas') {
        for (const checklistId of checklistIds) {
          if (on) checklists.add(checklistId)
          else checklists.delete(checklistId)
        }
      }
      if (!on) {
        for (const itemId of itemIds) checks.delete(itemId)
      }
      return { areas, checklists, checks }
    })
    setExcluded((current) => {
      const next = new Set(current)
      if (on || !coveredByArea) {
        for (const itemId of itemIds) next.delete(itemId)
      } else {
        for (const itemId of itemIds) next.add(itemId)
      }
      return next
    })
  }, [])

  const addChecks = useCallback((ids: readonly string[]) => {
    setTouched(true)
    setSets((current) => {
      const checks = new Set(current.checks)
      for (const id of ids) checks.add(id)
      return { ...current, checks }
    })
    setExcluded((current) => {
      const next = new Set(current)
      for (const id of ids) next.delete(id)
      return next
    })
  }, [])

  const removeChecks = useCallback((ids: readonly string[]) => {
    setTouched(true)
    setSets((current) => {
      const checks = new Set(current.checks)
      for (const id of ids) checks.delete(id)
      return { ...current, checks }
    })
    setExcluded((current) => {
      const next = new Set(current)
      for (const id of ids) next.add(id)
      return next
    })
  }, [])

  const selectAll = useCallback((scope: QaSelectionScope, ids: readonly string[]) => {
    setTouched(true)
    setExcluded(new Set())
    setSets((current) => ({ ...current, [scope]: new Set(ids) }))
  }, [])

  const clear = useCallback(() => {
    setExcluded(new Set())
    setSets(EMPTY)
  }, [])

  const visibleOf = useCallback((scope: QaSelectionScope, visibleIds: readonly string[]) => (
    visibleIds.filter((id) => sets[scope].has(id))
  ), [sets])

  // Optional chain: the historical one-arg isSelected(item.id) landmine call
  // must return false, never throw (see qaItpScope.test.tsx).
  const isSelected = useCallback((scope: QaSelectionScope, id: string) => (
    sets[scope]?.has(id) ?? false
  ), [sets])

  const isCheckIncluded = useCallback((
    id: string,
    checklistId?: string,
    areaId?: string,
  ) => {
    if (sets.checks.has(id)) return true
    const underChecklist = Boolean(checklistId && sets.checklists.has(checklistId))
    const underArea = Boolean(areaId && sets.areas.has(areaId))
    return (underChecklist || underArea) && !excluded.has(id)
  }, [sets, excluded])

  const toggleCheck = useCallback((id: string, checklistId?: string, areaId?: string) => {
    setTouched(true)
    const underChecklist = Boolean(checklistId && sets.checklists.has(checklistId))
    const underArea = Boolean(areaId && sets.areas.has(areaId))
    if (underChecklist || underArea) {
      setExcluded((current) => {
        const next = new Set(current)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return next
      })
      setSets((current) => {
        if (!current.checks.has(id)) return current
        const checks = new Set(current.checks)
        checks.delete(id)
        return { ...current, checks }
      })
      return
    }
    setSets((current) => {
      const checks = new Set(current.checks)
      if (checks.has(id)) checks.delete(id)
      else checks.add(id)
      return { ...current, checks }
    })
  }, [sets])

  const counts = useMemo(() => ({
    areas: sets.areas.size,
    checklists: sets.checklists.size,
    checks: sets.checks.size,
    total: sets.areas.size + sets.checklists.size + sets.checks.size,
  }), [sets])

  /**
   * Compatibility view of the old single-scope model: the most specific
   * populated scope. Verbs that only make sense for one scope at a time
   * (move, rename) key off this.
   */
  const scope: QaSelectionScope | null = useMemo(() => {
    if (sets.checks.size > 0) return 'checks'
    if (sets.checklists.size > 0) return 'checklists'
    if (sets.areas.size > 0) return 'areas'
    return null
  }, [sets])

  return {
    scope,
    sets,
    counts,
    toggle,
    setHeader,
    selectAll,
    addChecks,
    removeChecks,
    clear,
    isSelected,
    isCheckIncluded,
    toggleCheck,
    excludedChecks: excluded,
    visibleOf,
    touched,
  }
}

export type QaSelection = ReturnType<typeof useQaSelection>
